import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  Divider,
  TextField,
  useMediaQuery,
  useTheme,
  Collapse,
  Autocomplete,
} from "@mui/material";
import {
  ArrowDropDown as ArrowDropDownIcon,
  Description as FileIcon,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  StockLedgerList,
  StockLedgerExport,
  getCategories,
  getAllItems,
} from "../../../Services/Services";

const formatCurrency = (num) =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2 }).format(num || 0);

// ✅ Helper to calculate exact YYYY-MM-DD dates for any range option
const calculateDatesForRange = (rangeType, customFrom, customTo) => {
  const now = new Date();
  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff));
  };

  if (rangeType === "today") {
    const str = formatDate(now);
    return { from_date: str, to_date: str };
  }
  if (rangeType === "yesterday") {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const str = formatDate(y);
    return { from_date: str, to_date: str };
  }
  if (rangeType === "this_week") {
    const mon = getMonday(now);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { from_date: formatDate(mon), to_date: formatDate(sun) };
  }
  if (rangeType === "previous_week") {
    const thisMon = getMonday(now);
    const prevMon = new Date(thisMon);
    prevMon.setDate(thisMon.getDate() - 7);
    const prevSun = new Date(prevMon);
    prevSun.setDate(prevMon.getDate() + 6);
    return { from_date: formatDate(prevMon), to_date: formatDate(prevSun) };
  }
  if (rangeType === "this_month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from_date: formatDate(start), to_date: formatDate(end) };
  }
  if (rangeType === "current_year") {
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear(), 11, 31);
    return { from_date: formatDate(start), to_date: formatDate(end) };
  }
  if (rangeType === "financial_year") {
    let start, end;
    if (now.getMonth() >= 3) {
      start = new Date(now.getFullYear(), 3, 1);
      end = new Date(now.getFullYear() + 1, 2, 31);
    } else {
      start = new Date(now.getFullYear() - 1, 3, 1);
      end = new Date(now.getFullYear(), 2, 31);
    }
    return { from_date: formatDate(start), to_date: formatDate(end) };
  }
  if (rangeType === "custom" && customFrom && customTo) {
    return { from_date: customFrom, to_date: customTo };
  }
  return { from_date: "", to_date: "" };
};

const StockLedgerTable = () => {
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalIn, setTotalIn] = useState(0);
  const [totalOut, setTotalOut] = useState(0);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [excelLoading, setExcelLoading] = useState(false);

  // ✅ Infinite Scroll States
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const sentinelRef = useRef(null);
  const containerRef = useRef(null);

  // ✅ Date-range state
  const [dateRange, setDateRange] = useState("today");
  const [customDates, setCustomDates] = useState({ from: "", to: "" });
  const [menuEl, setMenuEl] = useState(null);

  // ✅ Category & Item Filter States
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedLedger, setExpandedLedger] = useState(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDates, setTempCustomDates] = useState({ from: "", to: "" });

  const formatDisplayDate = (d) => {
    if (!d) return "";
    const [y, m, day] = d.split("-");
    return `${day}-${m}-${y}`;
  };

  const getDateRangeDisplay = (rangeType) => {
    const dates = calculateDatesForRange(rangeType, customDates.from, customDates.to);
    if (!dates.from_date || !dates.to_date) return "";
    if (rangeType === "today" || rangeType === "yesterday") {
      return formatDisplayDate(dates.from_date);
    }
    return `${formatDisplayDate(dates.from_date)} to ${formatDisplayDate(dates.to_date)}`;
  };

  // 1. Categories Fetch
  useEffect(() => {
    const fetchCategoriesData = async () => {
      setLoadingCategories(true);
      try {
        const res = await getCategories(1, "");
        const catList = res?.results || (Array.isArray(res) ? res : []);
        setCategories(catList);
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategoriesData();
  }, []);

  // 2. Items Fetch
  useEffect(() => {
    const fetchItemsData = async () => {
      setLoadingItems(true);
      try {
        const filters = {};
        if (selectedCategory) {
          filters.category = selectedCategory.category_name || selectedCategory.name;
        }
        const res = await getAllItems(1, filters);
        const itemList = res?.results || (Array.isArray(res) ? res : []);
        setItems(itemList);
      } catch (err) {
        console.error("Failed to fetch items:", err);
      } finally {
        setLoadingItems(false);
      }
    };
    fetchItemsData();
  }, [selectedCategory]);

  // ============== Fetch Stock Ledger ==============
  const fetchStockLedger = async (params, isAppend = false) => {
    if (isAppend) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const res = await StockLedgerList(params);
      const totalRecords = res?.count || 0;
      setTotalCount(totalRecords);
      setHasMore(Boolean(res?.next));

      const innerRes = res?.results || {};
      const rows = innerRes?.results || (Array.isArray(res?.results) ? res?.results : []);

      if (isAppend) {
        setLedgerData((prev) => [...prev, ...(Array.isArray(rows) ? rows : [])]);
      } else {
        setLedgerData(Array.isArray(rows) ? rows : []);
      }

      setTotalIn(innerRes?.total_in || res?.total_in || 0);
      setTotalOut(innerRes?.total_out || res?.total_out || 0);
      setCurrentBalance(innerRes?.current_balance || res?.current_balance || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch stock ledger");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // ✅ 1. When filters change -> Reset page & data, fetch Page 1 with explicit dates
  useEffect(() => {
    setPage(1);
    const params = { page: 1 };
    params.date_range = dateRange;

    const dates = calculateDatesForRange(dateRange, customDates.from, customDates.to);
    if (dates.from_date && dates.to_date) {
      params.from_date = dates.from_date;
      params.to_date = dates.to_date;
    }

    if (selectedCategory) params.category = selectedCategory.id;
    if (selectedItem) params.item = selectedItem.id;

    const handler = setTimeout(() => {
      fetchStockLedger(params, false);
    }, 400);

    return () => clearTimeout(handler);
  }, [dateRange, customDates, selectedCategory, selectedItem]);

  // ✅ 2. When page changes (> 1) -> Fetch next page & append
  useEffect(() => {
    if (page === 1) return;

    const params = { page };
    params.date_range = dateRange;

    const dates = calculateDatesForRange(dateRange, customDates.from, customDates.to);
    if (dates.from_date && dates.to_date) {
      params.from_date = dates.from_date;
      params.to_date = dates.to_date;
    }

    if (selectedCategory) params.category = selectedCategory.id;
    if (selectedItem) params.item = selectedItem.id;

    fetchStockLedger(params, true);
  }, [page]);

  // ✅ 3. Intersection Observer for Infinite Scroll
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      {
        root: isMobile ? null : containerRef.current,
        threshold: 0.1,
      }
    );

    const currentSentinel = sentinelRef.current;
    if (currentSentinel) {
      observer.observe(currentSentinel);
    }

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, [loading, loadingMore, hasMore, isMobile]);

  const handleExport = async () => {
    setExcelLoading(true);
    try {
      const params = {};
      params.date_range = dateRange;
      const dates = calculateDatesForRange(dateRange, customDates.from, customDates.to);
      if (dates.from_date && dates.to_date) {
        params.from_date = dates.from_date;
        params.to_date = dates.to_date;
      }
      if (selectedCategory) params.category = selectedCategory.id;
      if (selectedItem) params.item = selectedItem.id;

      const blob = await StockLedgerExport(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Stock_Ledger_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Export failed");
    } finally {
      setExcelLoading(false);
    }
  };

  const handleReset = () => {
    setDateRange("today");
    setCustomDates({ from: "", to: "" });
    setTempCustomDates({ from: "", to: "" });
    setShowCustomDateInputs(false);
    setSelectedCategory(null);
    setSelectedItem(null);
    setPage(1);
  };

  const handleApplyCustomDateRange = () => {
    if (tempCustomDates.from && tempCustomDates.to) {
      setCustomDates({
        from: tempCustomDates.from,
        to: tempCustomDates.to,
      });
      setDateRange("custom");
      setMenuEl(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  const handleCancelCustomDateRange = () => {
    setTempCustomDates({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  return (
    <Box p={2} sx={{ mt: { xs: 4, sm: -3 }, pt: 0, mb: 2 }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ✅ Filters */}
      <Grid container spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3.5}>
          <TextField
            label="Date Range"
            value={
              dateRange === "custom" && customDates.from && customDates.to
                ? `${formatDisplayDate(customDates.from)} to ${formatDisplayDate(customDates.to)}`
                : dateRange === "today"
                  ? "Today"
                  : dateRange === "yesterday"
                    ? "Yesterday"
                    : dateRange === "this_week"
                      ? "This Week"
                      : dateRange === "previous_week"
                        ? "Previous Week"
                        : dateRange === "this_month"
                          ? "This Month"
                          : dateRange === "current_year"
                            ? "Current Year"
                            : dateRange === "financial_year"
                              ? "Financial Year"
                              : "Select Date Range"
            }
            onClick={(e) => setMenuEl(e.currentTarget)}
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton size="small" edge="end">
                  <ArrowDropDownIcon />
                </IconButton>
              ),
            }}
            sx={{
              cursor: "pointer",
              "& .MuiInputBase-root": {
                height: 35,
                borderRadius: 0,
                fontSize: "0.8rem",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.75rem",
              },
            }}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={categories}
            loading={loadingCategories}
            getOptionLabel={(option) => option.category_name || option.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            value={selectedCategory}
            onChange={(event, newValue) => {
              setSelectedCategory(newValue);
              setSelectedItem(null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                placeholder="All Categories"
                sx={{
                  "& .MuiInputBase-root": {
                    height: 35,
                    borderRadius: 0,
                    fontSize: "0.8rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            )}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3.5}>
          <Autocomplete
            size="small"
            options={items}
            loading={loadingItems}
            getOptionLabel={(option) =>
              option.name
                ? `${option.name} ${option.item_code ? `(${option.item_code})` : ""}`
                : option.item_name || ""
            }
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            value={selectedItem}
            onChange={(event, newValue) => setSelectedItem(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Item"
                placeholder="All Items"
                sx={{
                  "& .MuiInputBase-root": {
                    height: 35,
                    borderRadius: 0,
                    fontSize: "0.8rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            )}
            fullWidth
          />
        </Grid>

        <Grid item xs={8} sm={4} md={1.4}>
          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{
              height: "35px",
              whiteSpace: "nowrap",
              borderRadius: "0",
              fontSize: "0.75rem",
              textTransform: "none",
            }}
            fullWidth
          >
            Reset
          </Button>
        </Grid>

        <Grid item xs={4} sm={2} md={0.6} sx={{ textAlign: "center" }}>
          <Tooltip title={excelLoading ? "Exporting..." : "Download Excel"} arrow>
            <span>
              <IconButton
                color="success"
                size="small"
                onClick={handleExport}
                disabled={excelLoading}
                sx={{ border: "1px solid #c8e6c9", borderRadius: 1, p: 0.8 }}
              >
                {excelLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <FileIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Date Range Menu */}
      <Menu
        anchorEl={menuEl}
        open={Boolean(menuEl)}
        onClose={() => {
          setMenuEl(null);
          setShowCustomDateInputs(false);
          setTempCustomDates({ from: "", to: "" });
        }}
        PaperProps={{ sx: { width: 350, maxHeight: 500 } }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setDateRange(range.key);
              setCustomDates({ from: "", to: "" });
              setMenuEl(null);
              setShowCustomDateInputs(false);
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2">{range.label}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}>
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Select Date Range
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDates.from}
                onChange={(e) =>
                  setTempCustomDates((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: tempCustomDates.to || new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDates.to}
                onChange={(e) =>
                  setTempCustomDates((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDates.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            {tempCustomDates.from && tempCustomDates.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: "#2e7d32",
                  fontWeight: 500,
                }}
              >
                📅 {formatDisplayDate(tempCustomDates.from)} to{" "}
                {formatDisplayDate(tempCustomDates.to)}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDates.from || !tempCustomDates.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>

      {/* ✅ Table / Card View Rendering */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : ledgerData.length === 0 ? (
        <Box sx={{ textAlign: "center", p: 4 }}>
          <Typography color="text.secondary">No entries found</Typography>
        </Box>
      ) : isMobile ? (
        /* 📱 MOBILE VIEW */
        <Box sx={{ bgcolor: "background.paper" }}>
          {ledgerData.map((row, index) => (
            <Box key={row.id || index}>
              <Box
                onClick={() =>
                  setExpandedLedger(expandedLedger === row.id ? null : row.id)
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  cursor: "pointer",
                  bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                  "&:active": { bgcolor: "#e3f2fd" },
                  borderLeft: "4px solid #2196f3",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}>
                      #{index + 1}
                    </Typography>
                    <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}>
                      {row.item_name || "N/A"}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "#666",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>{row.transaction_type || "-"}</span>
                    <span style={{ color: "#999" }}>•</span>
                    <span style={{ fontSize: "0.7rem" }}>{row.date}</span>
                  </Typography>
                </Box>
                <IconButton size="small" sx={{ ml: 1 }}>
                  {expandedLedger === row.id ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              </Box>

              <Collapse in={expandedLedger === row.id} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                    borderLeft: "4px solid #2196f3",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 0.8,
                      mb: 1,
                    }}
                  >
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                        ITEM NAME
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#333" }}>
                        {row.item_name || "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                        DATE
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                        {row.date || "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                        CATEGORY
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                        {row.category_name || "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                        ITEM TYPE
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                        {row.item_type || "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                        GRN NO
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                        {row.grn_no || "-"}
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                        TRANSACTION TYPE
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                        {row.transaction_type || "-"}
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                        REFERENCE NO
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                        {row.reference_no || "-"}
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                        PROJECT NAME
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                        {row.project_name || "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                        QTY IN
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          color: row.quantity_in > 0 ? "#4caf50" : "#999",
                        }}
                      >
                        {row.quantity_in || "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                        QTY OUT
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          color: row.quantity_out > 0 ? "#f44336" : "#999",
                        }}
                      >
                        {row.quantity_out || "-"}
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                        BALANCE QTY
                      </Typography>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#2196f3" }}>
                        {row.balance_qty || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Collapse>
              <Divider />
            </Box>
          ))}

          {/* Mobile Infinite Scroll Sentinel */}
          <Box ref={sentinelRef} sx={{ textAlign: "center", py: 2 }}>
            {loadingMore && <CircularProgress size={24} />}
            {!hasMore && ledgerData.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Showing all {ledgerData.length} entries of {totalCount}
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        /* 💻 DESKTOP VIEW - Table */
        <TableContainer
          ref={containerRef}
          component={Paper}
          sx={{
            maxHeight: "73vh",
            overflow: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: 1000,
              "& td, & th": {
                textAlign: "center",
                whiteSpace: "nowrap",
                borderBottom: "1px solid #ccc",
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#f5f5f5",
                  "& th": {
                    fontWeight: "bold",
                    color: "#333",
                    borderBottom: "1px solid #999",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <TableCell>S.No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>GRN No.</TableCell>
                <TableCell>Transaction Type</TableCell>
                <TableCell>Reference No</TableCell>
                <TableCell>Project Name</TableCell>
                <TableCell>Qty In</TableCell>
                <TableCell>Qty Out</TableCell>
                <TableCell>Balance</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {ledgerData.map((row, index) => (
                <TableRow
                  key={row.id || index}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    "& td": {
                      paddingTop: "4px !important",
                      paddingBottom: "4px !important",
                      fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                    },
                    "&:hover": {
                      backgroundColor: "#e3f2fd !important",
                    },
                  }}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.date || "-"}</TableCell>
                  <TableCell>{row.category_name || "-"}</TableCell>
                  <TableCell>{row.item_name || "-"}</TableCell>
                  <TableCell>{row.grn_no || "-"}</TableCell>
                  <TableCell>{row.transaction_type || "-"}</TableCell>
                  <TableCell>{row.reference_no || "-"}</TableCell>
                  <TableCell>{row.project_name || "-"}</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: row.quantity_in > 0 ? "success.main" : "text.disabled",
                    }}
                  >
                    {row.quantity_in || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: row.quantity_out > 0 ? "error.main" : "text.disabled",
                    }}
                  >
                    {row.quantity_out || "-"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {row.balance_qty ?? 0}
                  </TableCell>
                </TableRow>
              ))}

              {/* Desktop Infinite Scroll Sentinel Row */}
              <TableRow ref={sentinelRef}>
                <TableCell colSpan={11} sx={{ textAlign: "center", py: 1.5, border: 0 }}>
                  {loadingMore ? (
                    <CircularProgress size={24} />
                  ) : !hasMore && ledgerData.length > 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      Showing all {ledgerData.length} entries of {totalCount}
                    </Typography>
                  ) : null}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default StockLedgerTable;